<template>
  <div
    ref="paypalWrapper"
    class="paypal-wrapper"
  >
    <template v-if="isLago">
      <PayPalButton
        v-show="!isLagoLoading"
        :key="lagoButtonKey"
        data-testid="paypal-btn"
        :get-client-id="getClientId"
        :create-billing-agreement="createBillingAgreement"
        :style-options="{ shape: 'pill', height: 55 }"
        @click="handleLagoClick"
        @approve="handleLagoApprove"
        @cancel="handleLagoCancel"
        @error="handleLagoError"
      />
      <div
        v-if="isLagoAuthorizing && !isLagoLoading"
        class="lago-paypal-overlay"
        data-testid="paypal-authorizing"
      >
        <CNuxtImg
          class="lago-paypal-overlay__loader"
          src="data:image/svg+xml;base64,PHN2ZyBjbGFzcz0ic3ZnLWxvYWRlciIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB3aWR0aD0iNTUiIGhlaWdodD0iNTUiIHZpZXdCb3g9IjAgMCA4MCA4MCI+PHBhdGggZmlsbD0iI2ZmZiIgZD0iTTQwIDcyQzIyLjQgNzIgOCA1Ny42IDggNDBTMjIuNCA4IDQwIDhzMzIgMTQuNCAzMiAzMmMwIDEuMS0uOSAyLTIgMnMtMi0uOS0yLTJjMC0xNS40LTEyLjYtMjgtMjgtMjhTMTIgMjQuNiAxMiA0MHMxMi42IDI4IDI4IDI4YzEuMSAwIDIgLjkgMiAycy0uOSAyLTIgMnoiPjxhbmltYXRlVHJhbnNmb3JtIGF0dHJpYnV0ZVR5cGU9InhtbCIgYXR0cmlidXRlTmFtZT0idHJhbnNmb3JtIiB0eXBlPSJyb3RhdGUiIGZyb209IjAgNDAgNDAiIHRvPSIzNjAgNDAgNDAiIGR1cj0iMC42cyIgcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiLz48L3BhdGg+PC9zdmc+"
          alt="loader"
        />
      </div>
      <base-button
        v-if="isLagoLoading"
        is-loading
        class="fake-paypal-button"
      />
    </template>
    <template v-else>
      <div
        v-show="!isShowFakeButton"
        id="paypal-button"
        ref="paypalButton"
        data-testid="paypal-btn"
      />
      <base-button
        v-if="isShowFakeButton"
        is-loading
        class="fake-paypal-button"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { PayPalButton } from '@appercut/lago-vue';
import type { UpsalePurchase } from '../interfaces/upsalePurchase.interface';

const props = defineProps({
  chargebeeId: {
    type: String,
    required: true,
  },
  discountPrice: {
    type: Number,
    required: true,
  },
  roundedPrice: {
    type: Number,
    required: true,
  },
  upsale: Boolean,
  requestPayerEmail: {
    type: Boolean,
    default: false,
  },
});
const emits = defineEmits<{
  'on-error': [error: unknown]
  'success': []
  'click-button': []
  'buy-upsale': [payload: UpsalePurchase]
  'action': []
  'availability': [available: boolean]
}>();

const gatewayStore = useKegelGatewayStore();
const isLago = computed(() => gatewayStore.provider === 'round');

const {
  isLoading: isLagoLoading,
  isAuthorizing: isLagoAuthorizing,
  buttonKey: lagoButtonKey,
  getClientId,
  handleClick: handleLagoClick,
  showAuthorizingLoader,
  hideAuthorizingLoader,
  createBillingAgreement,
  prefetchBaToken,
  handleApprove: handleLagoApprove,
  handleCancel: handleLagoCancel,
  handleError: handleLagoError,
} = useLagoPayPal({
  chargebeeId: () => props.chargebeeId,
  discountPrice: () => props.discountPrice,
  roundedPrice: () => props.roundedPrice,
  upsale: () => props.upsale,
  requestPayerEmail: () => props.requestPayerEmail,
  onSuccess: () => emits('success'),
  onUnavailable: () => emits('availability', false),
  onAvailable: () => emits('availability', true),
});

const paypalWrapper = ref<HTMLDivElement>();
const paypalButton = ref<HTMLDivElement>();
const { isShowFakeButton, isReady, isFailed, load, isPayPalButtonRendered } = usePayPal({
  chargebeeId: () => props.chargebeeId,
  discountPrice: () => props.discountPrice,
  roundedPrice: () => props.roundedPrice,
  upsale: () => !!props.upsale,
  requestPayerEmail: () => props.requestPayerEmail,
  containerRef: paypalButton,
  containerSelector: '#paypal-button',
  onClickButton: () => emits('click-button'),
  onSuccess: () => emits('success'),
  onAction: () => emits('action'),
});

const device = useDevice();
const background = computed(() => {
  return device.isIos && !isPayPalButtonRendered.value
    ? 'url(\'/images/paypal-plug.webp\') no-repeat top center'
    : 'none';
});

function isPayPalFrameFocused(): boolean {
  const active = document.activeElement;

  return active?.tagName === 'IFRAME' && !!paypalWrapper.value?.contains(active);
}

function onWindowBlur(): void {
  if (isLago.value && isPayPalFrameFocused()) {
    showAuthorizingLoader();
  }
}

function onWindowFocus(): void {
  if (isLago.value) {
    hideAuthorizingLoader();
  }
}

onMounted(() => {
  globalThis.addEventListener('blur', onWindowBlur);
  globalThis.addEventListener('focus', onWindowFocus);

  nextTick(() => {
    if (isLago.value) {
      void prefetchBaToken();
      return;
    }

    void load();
  });
});

onBeforeUnmount(() => {
  globalThis.removeEventListener('blur', onWindowBlur);
  globalThis.removeEventListener('focus', onWindowFocus);
});

watch([isFailed, isReady], () => {
  if (isFailed.value) emits('availability', false);
  if (isReady.value) emits('availability', true);
});
</script>

<style lang="scss" scoped>
  :deep(.zoid-outlet) {
    margin: 0 auto;
    display: block !important;
    transition: none !important;
  }

  :deep(.paypal-buttons) {
    display: block !important;
    width: 100%;
  }

  #paypal-button {
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    background: v-bind(background);
    background-size: auto 100%;
    background-position: center;
    background-repeat: no-repeat;
  }

  .paypal-wrapper {
    width: 100%;
    min-height: 55px;
    border-radius: 28px;
    position: relative;
  }

  .fake-paypal-button {
    height: 55px;
    min-height: 55px;
    border: 100px;
    background: $paypal;
    margin-right: auto;
    margin-left: auto;
    box-shadow: unset;
    border-radius: 100px;
  }

  .lago-paypal-overlay {
    position: absolute;
    inset: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $paypal;
    border-radius: 100px;
    pointer-events: none;

    &__loader {
      width: 30px;
      height: 30px;
    }
  }
</style>
